/**
 * Energy Cost Calculator JavaScript
 *
 * Client-side calculator using Ofgem Q1 2026 price cap rates.
 * No AJAX or jQuery needed - all calculations happen in the browser.
 *
 * To integrate into WordPress:
 * 1. Enqueue this script in functions.php
 */

(function() {
    'use strict';

    // Ofgem Q1 2026 Price Cap Rates (national average, direct debit)
    const RATES = {
        electricity: {
            unitRate: 0.2769,    // £ per kWh
            standingCharge: 0.5475 // £ per day
        },
        gas: {
            unitRate: 0.0593,    // £ per kWh
            standingCharge: 0.3509 // £ per day
        }
    };

    // Usage tiers (kWh/year)
    const TIERS = {
        low:           { electricity: 1800,  gas: 7500 },
        'low-medium':  { electricity: 2250,  gas: 9500 },
        medium:        { electricity: 2700,  gas: 11500 },
        'medium-high': { electricity: 3400,  gas: 14250 },
        high:          { electricity: 4100,  gas: 17000 }
    };

    const TIER_ORDER = ['low', 'low-medium', 'medium', 'medium-high', 'high'];

    // Property type to default tier mapping
    const PROPERTY_TIERS = {
        'flat':           'low',
        'mid-terrace':    'low-medium',
        'end-terrace':    'medium',
        'semi-detached':  'medium',
        'detached':       'medium-high',
        'large-detached': 'high',
        'bungalow':       'high'
    };

    // Helpers
    function $(selector) {
        return document.querySelector(selector);
    }

    function $$(selector) {
        return document.querySelectorAll(selector);
    }

    // State
    let currentStep = 1;
    let state = {
        fuelType: null,
        propertyType: null,
        baseTierIndex: 2,
        elecTierIndex: 2,
        gasTierIndex: 2
    };

    // DOM refs
    let calculator, progressBar;

    function init() {
        calculator = $('#energy-calculator');
        progressBar = $('#energy-calc-progress-bar');
        if (!calculator) return;
        bindEvents();
    }

    function bindEvents() {
        // Step 1: Fuel type selection (delegated)
        calculator.addEventListener('click', function(e) {
            var option = e.target.closest('.energy-calc-option[data-fuel]');
            if (option) handleFuelSelect(option);

            var propOption = e.target.closest('.energy-calc-option[data-property]');
            if (propOption) handlePropertySelect(propOption);
        });

        // Step 2: Back button
        $('#energy-calc-back-to-fuel').addEventListener('click', function() { goToStep(1); });

        // Step 3: Usage adjustment + back
        $('#energy-calc-back-to-property').addEventListener('click', function() { goToStep(2); });

        $('#energy-calc-elec-toggle').addEventListener('click', function(e) {
            var btn = e.target.closest('.energy-calc-tier-btn');
            if (btn) handleTierAdjust('electricity', btn);
        });

        $('#energy-calc-gas-toggle').addEventListener('click', function(e) {
            var btn = e.target.closest('.energy-calc-tier-btn');
            if (btn) handleTierAdjust('gas', btn);
        });

        $('#energy-calc-calculate-btn').addEventListener('click', calculateAndShowResults);

        // Step 4: Start again
        $('#energy-calc-start-again').addEventListener('click', resetCalculator);
    }

    function handleFuelSelect(option) {
        $$('.energy-calc-option[data-fuel]').forEach(function(el) { el.classList.remove('selected'); });
        option.classList.add('selected');
        state.fuelType = option.dataset.fuel;
        goToStep(2);
    }

    function handlePropertySelect(option) {
        $$('.energy-calc-option[data-property]').forEach(function(el) { el.classList.remove('selected'); });
        option.classList.add('selected');
        state.propertyType = option.dataset.property;

        var tierName = PROPERTY_TIERS[state.propertyType];
        var tierIndex = TIER_ORDER.indexOf(tierName);
        state.baseTierIndex = tierIndex;
        state.elecTierIndex = tierIndex;
        state.gasTierIndex = tierIndex;

        setupUsageStep();
        goToStep(3);
    }

    function setupUsageStep() {
        var hasElec = state.fuelType === 'electricity' || state.fuelType === 'both';
        var hasGas = state.fuelType === 'gas' || state.fuelType === 'both';

        $('#energy-calc-elec-usage').classList.toggle('d-none', !hasElec);
        $('#energy-calc-gas-usage').classList.toggle('d-none', !hasGas);

        resetToggle('#energy-calc-elec-toggle');
        resetToggle('#energy-calc-gas-toggle');

        updateUsageDisplay();
    }

    function resetToggle(selector) {
        var container = document.querySelector(selector);
        container.querySelectorAll('.energy-calc-tier-btn').forEach(function(btn) { btn.classList.remove('active'); });
        var typical = container.querySelector('.energy-calc-tier-btn[data-direction="typical"]');
        if (typical) typical.classList.add('active');
    }

    function handleTierAdjust(fuel, btn) {
        var direction = btn.dataset.direction;
        var toggle = btn.closest('.energy-calc-usage-toggle');

        toggle.querySelectorAll('.energy-calc-tier-btn').forEach(function(b) { b.classList.remove('active'); });
        btn.classList.add('active');

        var offset = direction === 'lower' ? -1 : direction === 'higher' ? 1 : 0;
        var newIndex = Math.max(0, Math.min(TIER_ORDER.length - 1, state.baseTierIndex + offset));

        if (fuel === 'electricity') {
            state.elecTierIndex = newIndex;
        } else {
            state.gasTierIndex = newIndex;
        }

        updateUsageDisplay();
    }

    function updateUsageDisplay() {
        var elecTier = TIER_ORDER[state.elecTierIndex];
        var gasTier = TIER_ORDER[state.gasTierIndex];

        $('#energy-calc-elec-kwh').textContent = formatNumber(TIERS[elecTier].electricity) + ' kWh/year';
        $('#energy-calc-gas-kwh').textContent = formatNumber(TIERS[gasTier].gas) + ' kWh/year';
    }

    function calculateAndShowResults() {
        var hasElec = state.fuelType === 'electricity' || state.fuelType === 'both';
        var hasGas = state.fuelType === 'gas' || state.fuelType === 'both';
        var annualTotal = 0;

        if (hasElec) {
            var elecTier = TIER_ORDER[state.elecTierIndex];
            var elecKwh = TIERS[elecTier].electricity;
            var elecUnitCost = elecKwh * RATES.electricity.unitRate;
            var elecStanding = RATES.electricity.standingCharge * 365;
            var elecTotal = elecUnitCost + elecStanding;

            $('#result-elec-kwh').textContent = formatNumber(elecKwh) + ' kWh';
            $('#result-elec-unit-cost').textContent = formatCurrency(elecUnitCost);
            $('#result-elec-standing').textContent = formatCurrency(elecStanding);
            $('#result-elec-total').textContent = formatCurrency(elecTotal);
            $('#energy-calc-elec-results').classList.remove('d-none');

            annualTotal += elecTotal;
        } else {
            $('#energy-calc-elec-results').classList.add('d-none');
        }

        if (hasGas) {
            var gasTier = TIER_ORDER[state.gasTierIndex];
            var gasKwh = TIERS[gasTier].gas;
            var gasUnitCost = gasKwh * RATES.gas.unitRate;
            var gasStanding = RATES.gas.standingCharge * 365;
            var gasTotal = gasUnitCost + gasStanding;

            $('#result-gas-kwh').textContent = formatNumber(gasKwh) + ' kWh';
            $('#result-gas-unit-cost').textContent = formatCurrency(gasUnitCost);
            $('#result-gas-standing').textContent = formatCurrency(gasStanding);
            $('#result-gas-total').textContent = formatCurrency(gasTotal);
            $('#energy-calc-gas-results').classList.remove('d-none');

            annualTotal += gasTotal;
        } else {
            $('#energy-calc-gas-results').classList.add('d-none');
        }

        $('#result-annual-total').textContent = formatCurrency(annualTotal);
        $('#result-monthly-total').textContent = formatCurrency(annualTotal / 12);

        goToStep(4);
    }

    function goToStep(step) {
        currentStep = step;

        // Update step indicators
        $$('.energy-calc-step').forEach(function(el) {
            var stepNum = parseInt(el.dataset.step, 10);
            el.classList.toggle('active', stepNum <= step);
            el.classList.toggle('completed', stepNum < step);
        });

        // Update progress bar
        progressBar.style.width = ((step / 4) * 100) + '%';

        // Show/hide step content
        $$('.energy-calc-step-content').forEach(function(el) { el.classList.add('d-none'); });
        $('#energy-calc-step-' + step).classList.remove('d-none');

        // Scroll to top of calculator
        window.scrollTo({
            top: calculator.getBoundingClientRect().top + window.pageYOffset - 100,
            behavior: 'smooth'
        });
    }

    function resetCalculator() {
        state = {
            fuelType: null,
            propertyType: null,
            baseTierIndex: 2,
            elecTierIndex: 2,
            gasTierIndex: 2
        };

        // Reset selections
        $$('.energy-calc-option').forEach(function(el) { el.classList.remove('selected'); });

        // Reset usage sections
        $('#energy-calc-elec-usage').classList.add('d-none');
        $('#energy-calc-gas-usage').classList.add('d-none');
        resetToggle('#energy-calc-elec-toggle');
        resetToggle('#energy-calc-gas-toggle');

        // Reset results
        $('#energy-calc-elec-results').classList.add('d-none');
        $('#energy-calc-gas-results').classList.add('d-none');

        goToStep(1);
    }

    function formatNumber(num) {
        return num.toLocaleString('en-GB');
    }

    function formatCurrency(amount) {
        return '\u00A3' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // Initialize when DOM is ready
    document.addEventListener('DOMContentLoaded', init);

})();


/**
 * Solar Savings Calculator JavaScript
 *
 * Client-side calculator using UK average solar data and Ofgem Q1 2026 rates.
 * No AJAX or jQuery needed - all calculations happen in the browser.
 *
 * To integrate into WordPress:
 * 1. Enqueue this script in functions.php
 */

(function() {
    'use strict';

    // Constants
    var ELECTRICITY_RATE = 0.2769;   // £ per kWh (Ofgem Q1 2026)
    var SEG_EXPORT_RATE = 0.15;      // £ per kWh (standard SEG flat rate)
    var KWH_PER_KWP = 900;          // UK average solar generation per kWp/year
    var SELF_CONSUMPTION_NO_BATTERY = 0.35;
    var SELF_CONSUMPTION_WITH_BATTERY = 0.75;
    var BATTERY_KWH = 9.5;

    // Electricity usage tiers (kWh/year) — same as energy calculator
    var ELEC_TIERS = [1800, 2250, 2700, 3400, 4100];

    // Property type data
    var PROPERTY_DATA = {
        'flat':           { systemKwp: 3.0, panels: 7,  panelsMin: 6,  panelsMax: 9,  costMin: 5000,  costMax: 6500  },
        'mid-terrace':    { systemKwp: 3.5, panels: 9,  panelsMin: 8,  panelsMax: 11, costMin: 5800,  costMax: 7400  },
        'end-terrace':    { systemKwp: 4.0, panels: 10, panelsMin: 9,  panelsMax: 13, costMin: 6500,  costMax: 8100  },
        'semi-detached':  { systemKwp: 4.5, panels: 11, panelsMin: 10, panelsMax: 14, costMin: 7500,  costMax: 9500  },
        'detached':       { systemKwp: 5.0, panels: 12, panelsMin: 11, panelsMax: 16, costMin: 8300,  costMax: 10200 },
        'large-detached': { systemKwp: 6.0, panels: 15, panelsMin: 13, panelsMax: 20, costMin: 10000, costMax: 12200 },
        'bungalow':       { systemKwp: 4.5, panels: 11, panelsMin: 10, panelsMax: 14, costMin: 7500,  costMax: 9500  }
    };

    // Property type to default electricity tier index
    var PROPERTY_TIER_INDEX = {
        'flat':           0,  // low
        'mid-terrace':    1,  // low-medium
        'end-terrace':    2,  // medium
        'semi-detached':  2,  // medium
        'detached':       3,  // medium-high
        'large-detached': 4,  // high
        'bungalow':       4   // high
    };

    // Helpers
    function $(selector) {
        return document.querySelector(selector);
    }

    function $$(selector) {
        return document.querySelectorAll(selector);
    }

    // State
    var currentStep = 1;
    var state = {
        propertyType: null,
        baseTierIndex: 2,
        elecTierIndex: 2,
        hasBattery: false
    };

    // DOM refs
    var calculator, progressBar;

    function init() {
        calculator = $('#solar-calculator');
        progressBar = $('#solar-calc-progress-bar');
        if (!calculator) return;
        bindEvents();
    }

    function bindEvents() {
        // Delegated click handler for option cards
        calculator.addEventListener('click', function(e) {
            var propOption = e.target.closest('.solar-calc-option[data-property]');
            if (propOption) handlePropertySelect(propOption);

            var batteryOption = e.target.closest('.solar-calc-battery-option[data-battery]');
            if (batteryOption) handleBatterySelect(batteryOption);
        });

        // Step 2: Usage toggle + back
        $('#solar-calc-back-to-property').addEventListener('click', function() { goToStep(1); });

        $('#solar-calc-elec-toggle').addEventListener('click', function(e) {
            var btn = e.target.closest('.solar-calc-tier-btn');
            if (btn) handleTierAdjust(btn);
        });

        $('#solar-calc-continue-to-battery').addEventListener('click', function() { goToStep(3); });

        // Step 3: Back
        $('#solar-calc-back-to-usage').addEventListener('click', function() { goToStep(2); });

        // Step 4: Start again
        $('#solar-calc-start-again').addEventListener('click', resetCalculator);
    }

    function handlePropertySelect(option) {
        $$('.solar-calc-option[data-property]').forEach(function(el) { el.classList.remove('selected'); });
        option.classList.add('selected');
        state.propertyType = option.dataset.property;

        var tierIndex = PROPERTY_TIER_INDEX[state.propertyType];
        state.baseTierIndex = tierIndex;
        state.elecTierIndex = tierIndex;

        setupUsageStep();
        goToStep(2);
    }

    function setupUsageStep() {
        resetToggle('#solar-calc-elec-toggle');
        updateUsageDisplay();
    }

    function resetToggle(selector) {
        var container = document.querySelector(selector);
        container.querySelectorAll('.solar-calc-tier-btn').forEach(function(btn) { btn.classList.remove('active'); });
        var typical = container.querySelector('.solar-calc-tier-btn[data-direction="typical"]');
        if (typical) typical.classList.add('active');
    }

    function handleTierAdjust(btn) {
        var direction = btn.dataset.direction;
        var toggle = btn.closest('.solar-calc-usage-toggle');

        toggle.querySelectorAll('.solar-calc-tier-btn').forEach(function(b) { b.classList.remove('active'); });
        btn.classList.add('active');

        var offset = direction === 'lower' ? -1 : direction === 'higher' ? 1 : 0;
        state.elecTierIndex = Math.max(0, Math.min(ELEC_TIERS.length - 1, state.baseTierIndex + offset));

        updateUsageDisplay();
    }

    function updateUsageDisplay() {
        var kwh = ELEC_TIERS[state.elecTierIndex];
        $('#solar-calc-elec-kwh').textContent = formatNumber(kwh) + ' kWh/year';
    }

    function handleBatterySelect(option) {
        $$('.solar-calc-battery-option').forEach(function(el) { el.classList.remove('selected'); });
        option.classList.add('selected');
        state.hasBattery = option.dataset.battery === 'true';

        calculateAndShowResults();
    }

    function calculateAndShowResults() {
        var data = PROPERTY_DATA[state.propertyType];
        var annualUsage = ELEC_TIERS[state.elecTierIndex];
        var annualGeneration = data.systemKwp * KWH_PER_KWP;
        var selfConsumptionRatio = state.hasBattery ? SELF_CONSUMPTION_WITH_BATTERY : SELF_CONSUMPTION_NO_BATTERY;

        // Savings formula
        var selfConsumedKwh = Math.min(annualGeneration * selfConsumptionRatio, annualUsage);
        var exportedKwh = annualGeneration - selfConsumedKwh;
        var savingsFromSelfConsumption = selfConsumedKwh * ELECTRICITY_RATE;
        var exportIncome = exportedKwh * SEG_EXPORT_RATE;
        var totalAnnualSaving = savingsFromSelfConsumption + exportIncome;
        var midpointCost = (data.costMin + data.costMax) / 2;
        var paybackYears = midpointCost / totalAnnualSaving;

        // System details
        $('#solar-result-system-size').textContent = data.systemKwp.toFixed(1) + ' kWp';
        $('#solar-result-panels').textContent = data.panelsMin + '–' + data.panelsMax + ' panels';
        $('#solar-result-generation').textContent = formatNumber(annualGeneration) + ' kWh';
        $('#solar-result-cost-range').textContent = formatCurrency(data.costMin) + ' \u2013 ' + formatCurrency(data.costMax);

        if (state.hasBattery) {
            $('#solar-result-battery-row').classList.remove('d-none');
            $('#solar-result-battery-size').textContent = BATTERY_KWH + ' kWh';
        } else {
            $('#solar-result-battery-row').classList.add('d-none');
        }

        // Savings breakdown
        $('#solar-result-usage').textContent = formatNumber(annualUsage) + ' kWh';
        $('#solar-result-self-consumed').textContent = formatNumber(Math.round(selfConsumedKwh)) + ' kWh';
        $('#solar-result-self-savings').textContent = formatCurrency(savingsFromSelfConsumption);
        $('#solar-result-exported').textContent = formatNumber(Math.round(exportedKwh)) + ' kWh';
        $('#solar-result-export-income').textContent = formatCurrency(exportIncome);

        // Totals
        $('#solar-result-annual-saving').textContent = formatCurrency(totalAnnualSaving);
        $('#solar-result-monthly-saving').textContent = formatCurrency(totalAnnualSaving / 12);
        $('#solar-result-payback').textContent = paybackYears.toFixed(1) + ' years';

        goToStep(4);
    }

    function goToStep(step) {
        currentStep = step;

        // Update step indicators
        $$('.solar-calc-step').forEach(function(el) {
            var stepNum = parseInt(el.dataset.step, 10);
            el.classList.toggle('active', stepNum <= step);
            el.classList.toggle('completed', stepNum < step);
        });

        // Update progress bar
        progressBar.style.width = ((step / 4) * 100) + '%';

        // Show/hide step content
        $$('.solar-calc-step-content').forEach(function(el) { el.classList.add('d-none'); });
        $('#solar-calc-step-' + step).classList.remove('d-none');

        // Scroll to top of calculator
        window.scrollTo({
            top: calculator.getBoundingClientRect().top + window.pageYOffset - 100,
            behavior: 'smooth'
        });
    }

    function resetCalculator() {
        state = {
            propertyType: null,
            baseTierIndex: 2,
            elecTierIndex: 2,
            hasBattery: false
        };

        // Reset selections
        $$('.solar-calc-option').forEach(function(el) { el.classList.remove('selected'); });
        $$('.solar-calc-battery-option').forEach(function(el) { el.classList.remove('selected'); });

        // Reset usage toggle
        resetToggle('#solar-calc-elec-toggle');

        goToStep(1);
    }

    function formatNumber(num) {
        return num.toLocaleString('en-GB');
    }

    function formatCurrency(amount) {
        return '\u00A3' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // Initialize when DOM is ready
    document.addEventListener('DOMContentLoaded', init);

})();


/**
 * Air Source Heat Pump Savings Calculator JavaScript
 *
 * Client-side calculator comparing gas boiler costs against ASHP.
 * Uses Ofgem Q1 2026 price cap rates and Energy Saving Trust data.
 *
 * To integrate into WordPress:
 * 1. Enqueue this script in functions.php
 */

(function() {
    'use strict';

    // Ofgem Q1 2026 Price Cap Rates
    var ELECTRICITY_RATE = 0.2769;   // £ per kWh (Ofgem Q1 2026)
    var GAS_UNIT_RATE = 0.0593;      // £ per kWh (Ofgem Q1 2026)
    var GAS_STANDING_CHARGE = 0.3509; // £ per day (Ofgem Q1 2026)
    var SCOP = 3.7;                  // Seasonal COP (Worcester Bosch units, mid-range 3.5–4.5)
    var BUS_GRANT = 7500;            // Boiler Upgrade Scheme grant

    // Gas usage tiers (kWh/year) — same as energy calculator
    var GAS_TIERS = [7500, 9500, 11500, 14250, 17000];

    // Boiler efficiency options
    var BOILER_EFFICIENCIES = {
        'new':     0.92,
        'typical': 0.85,
        'old':     0.70
    };

    // Property type data
    var PROPERTY_DATA = {
        'flat':           { heatPumpKw: 5,  heatPumpKwMin: 5,  heatPumpKwMax: 7,  costMin: 7000,  costMax: 9000,  tierIndex: 0 },
        'mid-terrace':    { heatPumpKw: 5,  heatPumpKwMin: 5,  heatPumpKwMax: 8,  costMin: 8000,  costMax: 10000, tierIndex: 1 },
        'end-terrace':    { heatPumpKw: 6,  heatPumpKwMin: 5,  heatPumpKwMax: 9,  costMin: 9000,  costMax: 11000, tierIndex: 2 },
        'semi-detached':  { heatPumpKw: 8,  heatPumpKwMin: 6,  heatPumpKwMax: 10, costMin: 10000, costMax: 13000, tierIndex: 2 },
        'detached':       { heatPumpKw: 10, heatPumpKwMin: 8,  heatPumpKwMax: 13, costMin: 12000, costMax: 15000, tierIndex: 3 },
        'large-detached': { heatPumpKw: 12, heatPumpKwMin: 10, heatPumpKwMax: 16, costMin: 14000, costMax: 18000, tierIndex: 4 },
        'bungalow':       { heatPumpKw: 8,  heatPumpKwMin: 6,  heatPumpKwMax: 11, costMin: 10000, costMax: 13000, tierIndex: 4 }
    };

    // Helpers
    function $(selector) {
        return document.querySelector(selector);
    }

    function $$(selector) {
        return document.querySelectorAll(selector);
    }

    // State
    var currentStep = 1;
    var state = {
        propertyType: null,
        baseTierIndex: 2,
        gasTierIndex: 2,
        boilerEfficiency: null
    };

    // DOM refs
    var calculator, progressBar;

    function init() {
        calculator = $('#ashp-calculator');
        progressBar = $('#ashp-calc-progress-bar');
        if (!calculator) return;
        bindEvents();
    }

    function bindEvents() {
        // Delegated click handler for option cards
        calculator.addEventListener('click', function(e) {
            var propOption = e.target.closest('.ashp-calc-option[data-property]');
            if (propOption) handlePropertySelect(propOption);

            var boilerOption = e.target.closest('.ashp-calc-boiler-option[data-boiler]');
            if (boilerOption) handleBoilerSelect(boilerOption);
        });

        // Step 2: Usage toggle + back
        $('#ashp-calc-back-to-property').addEventListener('click', function() { goToStep(1); });

        $('#ashp-calc-gas-toggle').addEventListener('click', function(e) {
            var btn = e.target.closest('.ashp-calc-tier-btn');
            if (btn) handleTierAdjust(btn);
        });

        $('#ashp-calc-continue-to-boiler').addEventListener('click', function() { goToStep(3); });

        // Step 3: Back
        $('#ashp-calc-back-to-usage').addEventListener('click', function() { goToStep(2); });

        // Step 4: Start again
        $('#ashp-calc-start-again').addEventListener('click', resetCalculator);
    }

    function handlePropertySelect(option) {
        $$('.ashp-calc-option[data-property]').forEach(function(el) { el.classList.remove('selected'); });
        option.classList.add('selected');
        state.propertyType = option.dataset.property;

        var data = PROPERTY_DATA[state.propertyType];
        state.baseTierIndex = data.tierIndex;
        state.gasTierIndex = data.tierIndex;

        setupUsageStep();
        goToStep(2);
    }

    function setupUsageStep() {
        resetToggle('#ashp-calc-gas-toggle');
        updateUsageDisplay();
    }

    function resetToggle(selector) {
        var container = document.querySelector(selector);
        container.querySelectorAll('.ashp-calc-tier-btn').forEach(function(btn) { btn.classList.remove('active'); });
        var typical = container.querySelector('.ashp-calc-tier-btn[data-direction="typical"]');
        if (typical) typical.classList.add('active');
    }

    function handleTierAdjust(btn) {
        var direction = btn.dataset.direction;
        var toggle = btn.closest('.ashp-calc-usage-toggle');

        toggle.querySelectorAll('.ashp-calc-tier-btn').forEach(function(b) { b.classList.remove('active'); });
        btn.classList.add('active');

        var offset = direction === 'lower' ? -1 : direction === 'higher' ? 1 : 0;
        state.gasTierIndex = Math.max(0, Math.min(GAS_TIERS.length - 1, state.baseTierIndex + offset));

        updateUsageDisplay();
    }

    function updateUsageDisplay() {
        var kwh = GAS_TIERS[state.gasTierIndex];
        $('#ashp-calc-gas-kwh').textContent = formatNumber(kwh) + ' kWh/year';
    }

    function handleBoilerSelect(option) {
        $$('.ashp-calc-boiler-option').forEach(function(el) { el.classList.remove('selected'); });
        option.classList.add('selected');
        state.boilerEfficiency = BOILER_EFFICIENCIES[option.dataset.boiler];

        calculateAndShowResults();
    }

    function calculateAndShowResults() {
        var data = PROPERTY_DATA[state.propertyType];
        var gasUsageKwh = GAS_TIERS[state.gasTierIndex];

        // Current gas heating cost (including standing charge)
        var gasUnitCost = gasUsageKwh * GAS_UNIT_RATE;
        var gasStandingCost = GAS_STANDING_CHARGE * 365;
        var currentGasCost = gasUnitCost + gasStandingCost;

        // Heat actually delivered by boiler
        var heatDelivered = gasUsageKwh * state.boilerEfficiency;

        // ASHP electricity needed to deliver same heat
        var ashpElecNeeded = heatDelivered / SCOP;
        var ashpRunningCost = ashpElecNeeded * ELECTRICITY_RATE;

        // Annual saving
        var annualSaving = currentGasCost - ashpRunningCost;

        // System cost & payback
        var midpointCost = (data.costMin + data.costMax) / 2;
        var costAfterGrant = Math.max(0, midpointCost - BUS_GRANT);
        var paybackYears = annualSaving > 0 ? costAfterGrant / annualSaving : 0;

        // Populate results — Current costs
        $('#ashp-result-gas-usage').textContent = formatNumber(gasUsageKwh) + ' kWh';
        $('#ashp-result-gas-unit-cost').textContent = formatCurrency(gasUnitCost);
        $('#ashp-result-gas-standing').textContent = formatCurrency(gasStandingCost);
        $('#ashp-result-current-total').textContent = formatCurrency(currentGasCost);

        // ASHP running cost
        $('#ashp-result-heat-delivered').textContent = formatNumber(Math.round(heatDelivered)) + ' kWh';
        $('#ashp-result-elec-needed').textContent = formatNumber(Math.round(ashpElecNeeded)) + ' kWh';
        $('#ashp-result-ashp-running-cost').textContent = formatCurrency(ashpRunningCost);

        // System details
        $('#ashp-result-system-size').textContent = data.heatPumpKwMin + '–' + data.heatPumpKwMax + ' kW (typically ' + data.heatPumpKw + ' kW)';
        $('#ashp-result-cost-range').textContent = formatCurrency(data.costMin) + ' \u2013 ' + formatCurrency(data.costMax);
        $('#ashp-result-bus-grant').textContent = '\u2212 ' + formatCurrency(BUS_GRANT);
        $('#ashp-result-cost-after-grant').textContent = formatCurrency(costAfterGrant);

        // Totals — handle positive and negative savings
        if (annualSaving > 0) {
            $('#ashp-calc-no-saving').classList.add('d-none');
            $('#ashp-calc-totals').classList.remove('d-none');
            $('#ashp-calc-totals-negative').classList.add('d-none');

            $('#ashp-result-annual-saving').textContent = formatCurrency(annualSaving);
            $('#ashp-result-monthly-saving').textContent = formatCurrency(annualSaving / 12);
            $('#ashp-result-payback').textContent = paybackYears.toFixed(1) + ' years';
        } else {
            $('#ashp-calc-no-saving').classList.remove('d-none');
            $('#ashp-calc-totals').classList.add('d-none');
            $('#ashp-calc-totals-negative').classList.remove('d-none');

            $('#ashp-result-extra-cost').textContent = formatCurrency(Math.abs(annualSaving));
            $('#ashp-result-extra-monthly').textContent = formatCurrency(Math.abs(annualSaving) / 12);
        }

        goToStep(4);
    }

    function goToStep(step) {
        currentStep = step;

        // Update step indicators
        $$('.ashp-calc-step').forEach(function(el) {
            var stepNum = parseInt(el.dataset.step, 10);
            el.classList.toggle('active', stepNum <= step);
            el.classList.toggle('completed', stepNum < step);
        });

        // Update progress bar
        progressBar.style.width = ((step / 4) * 100) + '%';

        // Show/hide step content
        $$('.ashp-calc-step-content').forEach(function(el) { el.classList.add('d-none'); });
        $('#ashp-calc-step-' + step).classList.remove('d-none');

        // Scroll to top of calculator
        window.scrollTo({
            top: calculator.getBoundingClientRect().top + window.pageYOffset - 100,
            behavior: 'smooth'
        });
    }

    function resetCalculator() {
        state = {
            propertyType: null,
            baseTierIndex: 2,
            gasTierIndex: 2,
            boilerEfficiency: null
        };

        // Reset selections
        $$('.ashp-calc-option').forEach(function(el) { el.classList.remove('selected'); });
        $$('.ashp-calc-boiler-option').forEach(function(el) { el.classList.remove('selected'); });

        // Reset usage toggle
        resetToggle('#ashp-calc-gas-toggle');

        goToStep(1);
    }

    function formatNumber(num) {
        return num.toLocaleString('en-GB');
    }

    function formatCurrency(amount) {
        return '\u00A3' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // Initialize when DOM is ready
    document.addEventListener('DOMContentLoaded', init);

})();